import { useContext, useState } from "react";
import {
  Check,
  AlertCircle,
  Clock,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { cn } from "@/lib/utils/helpers";
import { useIsBelowLg } from "../../hooks/useIsBelowLg";
import {
  getMilestoneDisplayStatus,
  getMilestoneProgressDisplayLabel,
  type ReimbursementMilestoneStatus,
} from "../../utils/reimbursementMilestoneStatus";
import {
  getReimbursementSectionActionMode,
  isSectionUnlockDisabled,
  shouldShowLibertyUnlockAction,
  resolveSectionWorkflowDisplayLabel,
  type ReimbursementSectionStatusValue,
} from "../../utils/reimbursementSectionStatus";
import { ReimbursementStatusBarExpandedContext } from "./ReimbursementStatusBarContext";
import {
  MilestoneSectionActions,
  MilestoneUnlockAction,
} from "./ReimbursementMilestoneSectionActions";
import { ReimbursementSectionSubmitConfirm } from "./ReimbursementSectionSubmitConfirm";
import type {
  ReimbursementDocumentStatus,
  ReimbursementStatusBarIncompleteItem,
  ReimbursementStatusBarProps,
} from "../../types/components";

interface Milestone {
  id: number;
  label: string;
}

type ItemStatus = "pending" | "uploaded" | "rejected";

function incompleteFieldItem(
  label: string,
  isDone: boolean,
): ReimbursementStatusBarIncompleteItem | null {
  if (isDone) return null;
  return { label, docStatus: "pending" };
}

function pushIncompleteField(
  items: ReimbursementStatusBarIncompleteItem[],
  label: string,
  isDone: boolean,
) {
  const item = incompleteFieldItem(label, isDone);
  if (item) items.push(item);
}

export function ReimbursementStatusBar({
  layout = "vertical",
  firstMonthRentUnpaid,
  reimbursementReqDate,
  reimbursementType,
  reason,
  signedLease,
  currentLedger,
  finalLedger,
  skipAffidavit,
  evictionLawsuit,
  alternateCourtDocument,
  caresActNotice,
  bankruptcyDocument,
  courtRegistry,
  stipulationAgreement,
  defaultStipulation,
  bondRegistryReceipt,
  caresActApplicable,
  bankruptcyApplicable,
  appealApplicable,
  stipulationApplicable,
  alternateCourtDocApplicable,
  usingThirdPartyEvictionServices,
  utilizingLegalTeamForEviction,
  thirdPartyServicerName,
  residentPaidIntoCourt,
  residentDefaultedOnRepayment,
  securityDepositCollected,
  securityDeposit,
  leaseModificationsAtMoveIn,
  skippedDuringEviction,
  originalLease,
  originalLedger,
  moveOutDate,
  newResidentLeaseStartDate,
  newResidentMoveInDate,
  paymentInfoConfirmed,
  showSectionActions,
  sectionActionMode,
  showMilestoneSaveSubmit = true,
  showDisabledUnlockForInProgress = false,
  isLibertyRole = false,
  isReimbursementConcluded = false,
  sectionUnlocked,
  sectionActionsLocked,
  submitDisabled,
  draftSaveDisabled,
  onSaveDraft,
  onSectionSubmit,
  onUnlockSection,
  sectionWorkflowStatus,
  sectionSubmitReady,
  sectionActionLoading,
}: ReimbursementStatusBarProps) {
  const isBelowLg = useIsBelowLg();
  const isExpandedInDrawer = useContext(ReimbursementStatusBarExpandedContext);
  const [mobileExpansion, setMobileExpansion] = useState({
    isBelowLg,
    isOpen: false,
  });
  const [pendingSubmitMilestoneId, setPendingSubmitMilestoneId] = useState<
    number | null
  >(null);
  const isOpen =
    mobileExpansion.isBelowLg === isBelowLg && mobileExpansion.isOpen;
  const showMilestones = isExpandedInDrawer || !isBelowLg || isOpen;

  const milestoneStatusInput = {
    firstMonthRentUnpaid,
    reimbursementReqDate,
    reimbursementType,
    reason,
    signedLease,
    currentLedger,
    finalLedger,
    skipAffidavit,
    evictionLawsuit,
    alternateCourtDocument,
    caresActNotice,
    bankruptcyDocument,
    courtRegistry,
    stipulationAgreement,
    defaultStipulation,
    bondRegistryReceipt,
    caresActApplicable,
    bankruptcyApplicable,
    appealApplicable,
    stipulationApplicable,
    alternateCourtDocApplicable,
    usingThirdPartyEvictionServices,
    utilizingLegalTeamForEviction,
    thirdPartyServicerName,
    residentPaidIntoCourt,
    residentDefaultedOnRepayment,
    securityDepositCollected,
    securityDeposit,
    leaseModificationsAtMoveIn,
    skippedDuringEviction,
    originalLease,
    originalLedger,
    moveOutDate,
    newResidentLeaseStartDate,
    newResidentMoveInDate,
    paymentInfoConfirmed,
  };

  const getMilestoneStatusForDisplay = (milestoneId: number) =>
    getMilestoneDisplayStatus(
      milestoneStatusInput,
      milestoneId,
      sectionWorkflowStatus?.[milestoneId as 1 | 2 | 3] ?? null,
    );

  const docItem = (
    doc: ReimbursementDocumentStatus,
    label: string,
  ): ReimbursementStatusBarIncompleteItem | null => {
    if (doc.status === "approved") return null;
    if (!doc.uploaded) return { label, docStatus: "pending" };
    if (doc.status === "incomplete" || doc.status === "rejected") {
      return { label, docStatus: "rejected" };
    }
    return { label, docStatus: "uploaded" };
  };

  // Incomplete row order mirrors ReimbursementRequestTab section layout.
  const getIncompleteItems = (
    milestoneId: number,
  ): ReimbursementStatusBarIncompleteItem[] => {
    const items: ReimbursementStatusBarIncompleteItem[] = [];

    if (milestoneId === 1) {
      pushIncompleteField(items, "Reimbursement Request Date", !!reimbursementReqDate);
      pushIncompleteField(items, "First Month Rent Unpaid", !!firstMonthRentUnpaid);
      pushIncompleteField(items, "Reimbursement Type", !!reimbursementType);
      pushIncompleteField(
        items,
        "Lease modifications at move-in",
        leaseModificationsAtMoveIn !== null,
      );
      if (leaseModificationsAtMoveIn === true) {
        const ol = docItem(originalLease, "Original Lease Agreement");
        if (ol) items.push(ol);
      }
      const sl = docItem(signedLease, "Signed Lease");
      if (sl) items.push(sl);
      const cl = docItem(currentLedger, "Current Ledger");
      if (cl) items.push(cl);
      pushIncompleteField(
        items,
        "Security deposit collected",
        securityDepositCollected !== null,
      );
      if (securityDepositCollected === true) {
        pushIncompleteField(items, "Security deposit amount", !!securityDeposit);
      }
    }

    if (milestoneId === 2) {
      if (!reimbursementType) {
        return [
          {
            label: "Select a reimbursement type first",
            docStatus: "pending",
          },
        ];
      }
      pushIncompleteField(items, "Incident Reason", !!reason);

      if (reimbursementType === "Skipped") {
        pushIncompleteField(
          items,
          "30-Day Notice to Vacate issued?",
          caresActApplicable !== null,
        );
        if (caresActApplicable === true) {
          const can = docItem(caresActNotice, "30-Day Notice to Vacate");
          if (can) items.push(can);
        }
        const sa = docItem(skipAffidavit, "Skip Affidavit");
        if (sa) items.push(sa);
      }

      if (reimbursementType === "Eviction") {
        if (skippedDuringEviction === true) {
          pushIncompleteField(
            items,
            "CARES Act 30-Day Notice applies",
            caresActApplicable !== null,
          );
          if (caresActApplicable === true) {
            const d = docItem(caresActNotice, "CARES Act Notice");
            if (d) items.push(d);
          }
          const sa = docItem(skipAffidavit, "Skip Affidavit");
          if (sa) items.push(sa);
        } else {
          pushIncompleteField(
            items,
            "CARES Act 30-Day Notice applies",
            caresActApplicable !== null,
          );
          if (caresActApplicable === true) {
            const d = docItem(caresActNotice, "CARES Act Notice");
            if (d) items.push(d);
          }

          pushIncompleteField(
            items,
            "Resident skipped during eviction",
            skippedDuringEviction !== null,
          );

          const el = docItem(evictionLawsuit, "Eviction Lawsuit");
          if (el) items.push(el);

          pushIncompleteField(
            items,
            "Alternate Court-Issued Document",
            alternateCourtDocApplicable !== null,
          );
          if (alternateCourtDocApplicable === true) {
            const acd = docItem(
              alternateCourtDocument,
              "Alternate Court-Issued Document",
            );
            if (acd) items.push(acd);
          }

          pushIncompleteField(
            items,
            "Using third-party eviction services",
            usingThirdPartyEvictionServices !== null,
          );
          if (usingThirdPartyEvictionServices === true) {
            pushIncompleteField(
              items,
              "Third-party servicer name",
              !!thirdPartyServicerName,
            );
          }

          pushIncompleteField(
            items,
            "Utilizing legal team/counsel",
            utilizingLegalTeamForEviction !== null,
          );

          pushIncompleteField(
            items,
            "Resident filed bankruptcy",
            bankruptcyApplicable !== null,
          );
          if (bankruptcyApplicable === true) {
            const d = docItem(bankruptcyDocument, "Bankruptcy Document");
            if (d) items.push(d);
          }

          pushIncompleteField(
            items,
            "Resident appealed eviction",
            appealApplicable !== null,
          );
          if (appealApplicable === true) {
            const d = docItem(courtRegistry, "Appeal Document");
            if (d) items.push(d);
            pushIncompleteField(
              items,
              "Resident paid into court",
              residentPaidIntoCourt !== null,
            );
            if (residentPaidIntoCourt === true) {
              const br = docItem(
                bondRegistryReceipt,
                "Court Registry (Rent Bond)",
              );
              if (br) items.push(br);
            }
          }

          pushIncompleteField(
            items,
            "Court-ordered Stipulation",
            stipulationApplicable !== null,
          );
          if (stipulationApplicable === true) {
            const d = docItem(stipulationAgreement, "Stipulation Agreement");
            if (d) items.push(d);
            pushIncompleteField(
              items,
              "Resident defaulted on Stipulation Agreement",
              residentDefaultedOnRepayment !== null,
            );
            if (residentDefaultedOnRepayment === true) {
              const dd = docItem(defaultStipulation, "Default Stipulation");
              if (dd) items.push(dd);
            }
          }
        }
      }
    }

    if (milestoneId === 3) {
      pushIncompleteField(
        items,
        "Move-out Date",
        Boolean(String(moveOutDate ?? "").trim()),
      );
      if (leaseModificationsAtMoveIn === true) {
        const ol = docItem(originalLedger, "Original Ledger");
        if (ol) items.push(ol);
      }
      const fl = docItem(finalLedger, "Final Ledger");
      if (fl) items.push(fl);
      pushIncompleteField(
        items,
        "Payment information verified",
        paymentInfoConfirmed === true,
      );
    }

    return items;
  };

  const milestones: Milestone[] = [
    { id: 1, label: "Core Information" },
    { id: 2, label: "Unique Requirements" },
    { id: 3, label: "Final Information" },
  ];

  const getStatusConfig = (status: ReimbursementMilestoneStatus) => {
    const label = getMilestoneProgressDisplayLabel(status);
    if (status === "complete") {
      return {
        bg: "bg-green-500",
        text: "text-green-700",
        subtext: "text-green-600",
        label,
      };
    }
    if (status === "rejected") {
      return {
        bg: "bg-red-500",
        text: "text-red-700",
        subtext: "text-red-500",
        label,
      };
    }
    if (status === "partial") {
      return {
        bg: "bg-amber-400",
        text: "text-amber-700",
        subtext: "text-amber-600",
        label,
      };
    }
    return {
      bg: "bg-gray-200",
      text: "text-gray-500",
      subtext: "text-gray-400",
      label,
    };
  };

  const itemStatusConfig = (s: ItemStatus) => {
    if (s === "rejected") {
      return {
        dot: "bg-red-400",
        badge: "bg-red-50 text-red-600 border-red-200",
        text: "Incomplete",
      };
    }
    if (s === "uploaded") {
      return {
        dot: "bg-blue-400",
        badge: "bg-blue-50 text-blue-600 border-blue-200",
        text: "Uploaded",
      };
    }
    return {
      dot: "bg-gray-300",
      badge: "bg-gray-50 text-gray-500 border-gray-200",
      text: "Pending",
    };
  };

  const isVertical = layout === "vertical";
  const containerClass = isVertical
    ? "flex flex-col"
    : "flex flex-col md:flex-row md:items-start";

  return (
    <>
      <div
        className="w-full rounded-xl border border-slate-300 bg-white shadow-sm"
        data-testid="reimbursement-status-bar"
      >
      {isBelowLg && !isExpandedInDrawer ? (
        <button
          type="button"
          onClick={() => setMobileExpansion({ isBelowLg, isOpen: !isOpen })}
          className="flex w-full items-center justify-between p-4 lg:hidden"
          aria-expanded={isOpen}
          data-testid="reimbursement-status-bar-toggle"
        >
          <span className="text-sm font-semibold text-slate-800">
            Form Progress
          </span>
          <span aria-hidden="true">
            {isOpen ? (
              <ChevronUp className="h-5 w-5 stroke-[2.25] text-[#1597ff]" />
            ) : (
              <ChevronDown className="h-5 w-5 stroke-[2.25] text-slate-500" />
            )}
          </span>
        </button>
      ) : null}

      {showMilestones ? (
        <div
          className={cn(
            "px-4 pb-4",
            isBelowLg && !isExpandedInDrawer
              ? "border-t border-slate-200 pt-2"
              : "pt-4",
          )}
          data-testid="reimbursement-status-bar-milestones"
        >
          {isVertical ? (
            <header
              className="mb-4 border-b border-slate-200 pb-4"
              data-testid="reimbursement-status-bar-header"
            >
              {!isExpandedInDrawer ? (
                <h2 className="text-base font-bold leading-snug text-[#173f7a]">
                  Requirements Checklist
                </h2>
              ) : null}
              <p
                className={cn(
                  "text-sm leading-snug text-slate-600",
                  !isExpandedInDrawer ? "mt-1" : "",
                )}
              >
                Complete the form to satisfy the requirements below.
              </p>
            </header>
          ) : null}
          <div className={containerClass}>
            {milestones.map((milestone, index) => {
              const workflowStatus: ReimbursementSectionStatusValue =
                sectionWorkflowStatus?.[milestone.id as 1 | 2 | 3] ?? null;
              const status = getMilestoneStatusForDisplay(milestone.id);
              const cfg = getStatusConfig(status);
              const workflowLabel = resolveSectionWorkflowDisplayLabel(
                workflowStatus,
                status,
              );
              const milestoneItems = getIncompleteItems(milestone.id);
              const hasItems = milestoneItems.length > 0;
              const isLast = index === milestones.length - 1;
              const milestoneActionMode =
                sectionActionMode ??
                getReimbursementSectionActionMode(isLibertyRole, workflowStatus);
              const showActionsInThisSurface = showSectionActions && !isBelowLg;
              const showUnlock =
                shouldShowLibertyUnlockAction(
                  isLibertyRole,
                  isReimbursementConcluded,
                  workflowStatus,
                ) ||
                (showDisabledUnlockForInProgress &&
                  isLibertyRole &&
                  !isReimbursementConcluded);
              const unlockDisabled =
                Boolean(sectionActionsLocked) ||
                isSectionUnlockDisabled(workflowStatus, true) ||
                Boolean(sectionUnlocked?.[milestone.id as 1 | 2 | 3]);

              return (
                <div
                  key={milestone.id}
                  className={
                    isVertical
                      ? "flex min-w-0 flex-col"
                      : "flex min-w-0 md:flex-1"
                  }
                >
                  <div className="flex min-w-0 flex-col">
                    <div className="rounded-lg px-1 py-2">
                      <div className="flex items-start gap-2">
                        <div
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${cfg.bg}`}
                        >
                          {status === "complete" && (
                            <Check
                              size={13}
                              className="text-white"
                              strokeWidth={2.5}
                            />
                          )}
                          {status === "rejected" && (
                            <AlertCircle size={13} className="text-white" />
                          )}
                          {status === "partial" && (
                            <span className="h-2 w-2 rounded-full bg-white opacity-90" />
                          )}
                          {status === "pending" && (
                            <Clock size={11} className="text-gray-400" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold leading-snug text-slate-800">
                            {milestone.label}
                          </p>
                          <p
                            className={`mt-0.5 text-xs font-medium ${cfg.subtext}`}
                          >
                            {workflowLabel}
                          </p>
                        </div>
                      </div>
                    </div>

                    {showActionsInThisSurface && showUnlock ? (
                      <MilestoneUnlockAction
                        milestoneId={milestone.id}
                        unlockDisabled={unlockDisabled}
                        unlockLoading={
                          sectionActionLoading?.milestoneId === milestone.id &&
                          sectionActionLoading.kind === "unlock"
                        }
                        onUnlock={() => onUnlockSection?.(milestone.id)}
                      />
                    ) : null}
                    {showActionsInThisSurface &&
                    showMilestoneSaveSubmit &&
                    milestoneActionMode === "pmc" &&
                    workflowStatus !== "APPROVED" ? (
                      <MilestoneSectionActions
                        milestoneId={milestone.id}
                        status={status}
                        sectionWorkflowStatus={workflowStatus}
                        sectionSubmitReady={
                          sectionSubmitReady?.[milestone.id as 1 | 2 | 3] ?? false
                        }
                        submitDisabled={submitDisabled}
                        draftSaveDisabled={draftSaveDisabled}
                        saveLoading={
                          sectionActionLoading?.milestoneId === milestone.id &&
                          sectionActionLoading.kind === "draft"
                        }
                        submitLoading={
                          sectionActionLoading?.milestoneId === milestone.id &&
                          sectionActionLoading.kind === "submit"
                        }
                        onSaveDraft={() => onSaveDraft?.(milestone.id)}
                        onSubmitClick={() =>
                          setPendingSubmitMilestoneId(milestone.id)
                        }
                      />
                    ) : null}

                    {hasItems ? (
                      <div className="mx-0 mb-1.5 overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                        <ul className="space-y-0 py-1.5">
                          {milestoneItems.map((item, idx) => {
                            const itemStatus = item.docStatus ?? "pending";
                            const statusCfg = itemStatusConfig(itemStatus);

                            return (
                              <li
                                key={idx}
                                className="flex items-center justify-between gap-1.5 px-2 py-1"
                              >
                                <div className="flex min-w-0 items-center gap-1.5">
                                  <span
                                    className={`h-1.5 w-1.5 shrink-0 rounded-full ${statusCfg.dot}`}
                                  />
                                  <span className="truncate text-xs leading-tight text-slate-600">
                                    {item.label}
                                  </span>
                                </div>
                                <span
                                  className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${statusCfg.badge}`}
                                >
                                  {statusCfg.text}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ) : null}

                    {!isLast && isVertical ? (
                      <div className="flex justify-center py-0.5">
                        <div
                          className="h-3 w-0.5 rounded-full transition-colors duration-300"
                          style={{
                            backgroundColor:
                              status === "complete" ? "#86efac" : "#e5e7eb",
                          }}
                        />
                      </div>
                    ) : null}

                    {!isLast && !isVertical ? (
                      <div className="my-0.5 flex justify-center md:hidden">
                        <div
                          className="h-3 w-0.5 rounded-full transition-colors duration-300"
                          style={{
                            backgroundColor:
                              status === "complete" ? "#86efac" : "#e5e7eb",
                          }}
                        />
                      </div>
                    ) : null}
                  </div>

                  {!isLast && !isVertical ? (
                    <div className="hidden shrink-0 items-start px-2 pt-3.5 md:flex">
                      <div
                        className="h-0.5 w-8 rounded-full transition-colors duration-300"
                        style={{
                          backgroundColor:
                            status === "complete" ? "#86efac" : "#e5e7eb",
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
      </div>

      {!isBelowLg ? (
        <ReimbursementSectionSubmitConfirm
          pendingMilestoneId={pendingSubmitMilestoneId}
          onConfirm={(milestoneId) => onSectionSubmit?.(milestoneId)}
          onDismiss={() => setPendingSubmitMilestoneId(null)}
        />
      ) : null}
    </>
  );
}
